import { useState, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Globe } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Button } from '@/ui/components/common/Button'
import { Label } from '@/ui/components/common/Label'
import { Input, Textarea } from '@/ui/components/forms/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/forms/Select'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { classNames } from '@/ui/utils/main'
import { useGetSiteIdentity, useUpdateSiteIdentity } from '@/application/services/settings.service'
import { themes, envDefaultTheme } from '@/themes'
import type { HomepageLayout } from '@/domains/types'

interface SiteIdentityFormState {
  siteName: string
  siteDescription: string
  activeTheme: string
  publicTheme: string
  homepageLayout: HomepageLayout
}

interface SupportedLanguage {
  code: string
  label: string
}

const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
]

function normalizeLang(input: string): string {
  const code = input.substring(0, 2).toLowerCase()
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === code) ? code : 'fr'
}

export function SiteIdentitySection() {
  const { t, i18n } = useTranslation()
  const [selectedLang, setSelectedLang] = useState<string>(() => normalizeLang(i18n.language))
  const { data: settings, isLoading } = useGetSiteIdentity(selectedLang)
  const { mutateAsync: updateSettings, isPending } = useUpdateSiteIdentity(selectedLang)

  const [form, setForm] = useState<SiteIdentityFormState>({
    siteName: '',
    siteDescription: '',
    activeTheme: envDefaultTheme,
    publicTheme: envDefaultTheme,
    homepageLayout: 'classic',
  })

  useEffect(() => {
    if (settings) {
      setForm({
        siteName: settings.siteName,
        siteDescription: settings.siteDescription,
        activeTheme: settings.activeTheme,
        publicTheme: settings.publicTheme,
        homepageLayout: settings.homepageLayout,
      })
    }
  }, [settings])

  function handleSiteNameChange(event: ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, siteName: event.target.value }))
  }

  function handleDescriptionChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, siteDescription: event.target.value }))
  }

  function handleThemeChange(value: string) {
    setForm((prev) => ({ ...prev, activeTheme: value }))
  }

  function handlePublicThemeChange(value: string) {
    setForm((prev) => ({ ...prev, publicTheme: value }))
  }

  function handleHomepageLayoutChange(value: string) {
    setForm((prev) => ({ ...prev, homepageLayout: value === 'news' ? 'news' : 'classic' }))
  }

  async function handleSave() {
    try {
      await updateSettings({
        siteName: form.siteName,
        siteDescription: form.siteDescription,
        activeTheme: form.activeTheme,
        publicTheme: form.publicTheme,
        homepageLayout: form.homepageLayout,
      })
      toast.success(t('admin.settings_site_saved'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          {t('admin.settings_site_title')}
        </CardTitle>
        <CardDescription>{t('admin.settings_site_card_desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Language tabs — always visible, even while loading */}
        <div className="flex items-center gap-2">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setSelectedLang(lang.code)}
              className={classNames(
                'px-3 py-1 rounded-full text-sm font-medium transition-colors border',
                selectedLang === lang.code
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
              )}
            >
              {lang.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-64" />
          </>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="site-name">{t('admin.settings_site_name_label')}</Label>
              <Input
                id="site-name"
                value={form.siteName}
                onChange={handleSiteNameChange}
                placeholder={t('app_name')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="site-description">{t('admin.settings_site_description_label')}</Label>
              <Textarea
                id="site-description"
                value={form.siteDescription}
                onChange={handleDescriptionChange}
                placeholder={t('about.description', { appName: t('app_name') })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="theme-select">{t('admin.settings_theme_label')}</Label>
              <p className="text-xs text-muted-foreground">{t('admin.settings_theme_desc')}</p>
              <Select value={form.activeTheme} onValueChange={handleThemeChange}>
                <SelectTrigger id="theme-select" className="w-full md:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {themes.map((theme) => (
                    <SelectItem key={theme.id} value={theme.id}>
                      {theme.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="public-theme-select">{t('admin.settings_public_theme_label')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('admin.settings_public_theme_desc')}
              </p>
              <Select value={form.publicTheme} onValueChange={handlePublicThemeChange}>
                <SelectTrigger id="public-theme-select" className="w-full md:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {themes.map((theme) => (
                    <SelectItem key={theme.id} value={theme.id}>
                      {theme.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="homepage-layout-select">
                {t('admin.settings_homepage_layout_label')}
              </Label>
              <Select value={form.homepageLayout} onValueChange={handleHomepageLayoutChange}>
                <SelectTrigger id="homepage-layout-select" className="w-full md:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="classic">
                    {t('admin.settings_homepage_layout_classic')}
                  </SelectItem>
                  <SelectItem value="news">{t('admin.settings_homepage_layout_news')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => void handleSave()} isLoading={isPending}>
                {t('admin.settings_site_save_button')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
